import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Plus, Loader2, Pencil, Trash2, CalendarSearch, X, Check, Link2, Timer, Layers, ExternalLink, Upload } from "lucide-react";

import { useAuth, api } from "@/lib/auth";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PortalShell } from "@/components/account/PortalShell";

const CATEGORIES = [
    { key: "cat1", label: "Category 1" },
    { key: "cat2", label: "Category 2" },
];

const money = (cents) => `$${((cents || 0) / 100).toLocaleString("en-AU", { minimumFractionDigits: 0 })}`;

const empty = { name: "", location: "", city: "", category: "cat2", daily_rate_cents: 0, status: "active", notes: "", photo_url: "", photo_urls: [], lat: "", lng: "" };

export default function AdminScreens() {
    usePageMeta({ title: "Screens · NOXN CRM", description: "Manage digital billboard inventory." });
    const { user } = useAuth();

    const [screens, setScreens] = useState([]);
    const [invQuery, setInvQuery] = useState("");
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null = closed; {} = new; {...} = edit
    const [busy, setBusy] = useState(false);
    const [avScreen, setAvScreen] = useState(null); // selected screen for availability panel
    const [avRange, setAvRange] = useState({ start: "", end: "" });
    const [avResult, setAvResult] = useState(null);
    const [avBusy, setAvBusy] = useState(false);
    const [tab, setTab] = useState("screens"); // "screens" | "bookings"

    const isAdmin = user && user.role !== "client";

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/screens");
            setScreens(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not load screens.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openNew = () => setEditing({ ...empty });
    const openEdit = (s) => {
        // Migrate legacy single-photo screens to the array shape on open.
        const urls = Array.isArray(s.photo_urls) && s.photo_urls.length
            ? s.photo_urls
            : (s.photo_url ? [s.photo_url] : []);
        setEditing({ ...s, photo_urls: urls });
    };
    const closeForm = () => setEditing(null);

    const saveScreen = async (e) => {
        e?.preventDefault?.();
        if (!editing) return;
        const urls = (editing.photo_urls || []).filter(Boolean).slice(0, 5);
        const payload = {
            name: editing.name?.trim(),
            location: editing.location?.trim(),
            city: editing.city?.trim(),
            category: editing.category,
            daily_rate_cents: Number(editing.daily_rate_cents) || 0,
            status: editing.status || "active",
            notes: editing.notes || null,
            photo_urls: urls,
            photo_url: urls[0] || null,
            lat: editing.lat === "" || editing.lat === null || Number.isNaN(Number(editing.lat)) ? null : Number(editing.lat),
            lng: editing.lng === "" || editing.lng === null || Number.isNaN(Number(editing.lng)) ? null : Number(editing.lng),
            market_segment: editing.market_segment || null,
            maintenance_status: editing.maintenance_status || "operational",
            resolution: editing.resolution || null,
            physical_size: editing.physical_size || null,
            format: editing.format || null,
            daily_impressions: editing.daily_impressions == null || editing.daily_impressions === "" ? null : Number(editing.daily_impressions),
            media_owner: editing.media_owner || null,
            media_owner_contact: editing.media_owner_contact || null,
            media_requirements: editing.media_requirements || null,
            cost_price_cents: editing.cost_price_cents ?? null,
            peak_multiplier: editing.peak_multiplier ?? null,
            play_weight: editing.play_weight == null || editing.play_weight === "" ? null : Number(editing.play_weight),
            timezone: editing.timezone || null,
        };
        if (!payload.name || !payload.location || !payload.city) {
            toast.error("Name, location and city are required."); return;
        }
        setBusy(true);
        try {
            if (editing.id) {
                await api.patch(`/screens/${editing.id}`, payload);
                toast.success("Screen updated.");
            } else {
                await api.post("/screens", payload);
                toast.success("Screen added.");
            }
            closeForm();
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed.");
        } finally { setBusy(false); }
    };

    const deleteScreen = async (s) => {
        if (!window.confirm(`Delete "${s.name}"?\nIf bookings exist, it is set inactive instead.`)) return;
        try {
            await api.delete(`/screens/${s.id}`);
            toast.success("Screen removed.");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed.");
        }
    };

    const runAvailability = async (e) => {
        e?.preventDefault?.();
        if (!avScreen || !avRange.start || !avRange.end) {
            toast.error("Pick a screen and a date range."); return;
        }
        setAvBusy(true);
        setAvResult(null);
        try {
            const { data } = await api.get("/screens/availability/check", {
                params: { screen_id: avScreen.id, start: avRange.start, end: avRange.end },
            });
            setAvResult(data?.[0] || null);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Check failed.");
        } finally { setAvBusy(false); }
    };

    const stats = useMemo(() => ({
        total: screens.length,
        active: screens.filter((s) => s.status === "active").length,
        cat1: screens.filter((s) => s.category === "cat1").length,
    }), [screens]);

    return (
        <PortalShell title="Screens" mainClassName="p-0">
        <div className="min-h-screen w-full relative">
            <div className="absolute inset-0 -z-10 grid-noise opacity-25" />

            <main className="mx-auto max-w-7xl px-6 lg:px-10 py-12">
                <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.7, ease: [0.16,1,0.3,1] }}>
                    <div className="font-mono-tech text-[11px] uppercase tracking-[0.35em] text-[#FF5A00] mb-4 flex items-center gap-3">
                        <span className="inline-block w-6 h-px bg-[#FF5A00]" /> Inventory · Screens
                    </div>
                    <div className="flex flex-wrap items-end justify-between gap-6">
                        <h1 className="font-display font-black tracking-tighter text-white text-4xl sm:text-5xl leading-[0.95]">
                            Screen inventory.
                        </h1>
                        {isAdmin && (
                            <div className="flex flex-wrap items-center gap-2">
                                <ImportCsvButton onDone={load} />
                                <button onClick={openNew} data-testid="screens-new-btn"
                                    className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] text-white px-5 py-3 text-xs font-semibold uppercase tracking-widest transition-colors">
                                    <Plus size={14} /> New screen
                                </button>
                            </div>
                        )}
                    </div>
                    <p className="mt-4 text-slate-400 text-sm max-w-xl">
                        Every campaign booked through NOXN reserves inventory here. Dates that overlap an existing hold are rejected server-side.
                    </p>
                </motion.div>

                {/* Stats */}
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {[
                        { k: "Screens", v: stats.total },
                        { k: "Active", v: stats.active },
                        { k: "Category 1", v: stats.cat1 },
                    ].map((c, i) => (
                        <div key={i} className="p-6 bg-[#1B2E55] border border-white/10">
                            <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-500">/ {c.k}</div>
                            <div className="mt-2 font-display font-black tracking-tighter text-3xl text-white">{c.v}</div>
                        </div>
                    ))}
                </div>

                {/* Tabs */}
                <div className="mt-10 border-b border-white/10 flex items-center gap-1">
                    {[
                        { key: "screens", label: "Screens", Icon: Layers },
                        { key: "bookings", label: "Bookings", Icon: Link2 },
                    ].map(({ key, label, Icon }) => (
                        <button
                            key={key}
                            onClick={() => setTab(key)}
                            data-testid={`screens-tab-${key}`}
                            className={`inline-flex items-center gap-2 px-4 py-3 text-xs font-mono-tech uppercase tracking-widest border-b-2 -mb-px transition-colors ${
                                tab === key ? "border-[#FF5A00] text-white" : "border-transparent text-slate-500 hover:text-white"
                            }`}
                        >
                            <Icon size={12} /> {label}
                        </button>
                    ))}
                </div>

                {tab === "screens" && (
                <>
                {/* Availability panel */}
                <div className="mt-10 border border-[#FF5A00]/30 bg-[#1B2E55]/60 p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <CalendarSearch size={16} className="text-[#FF5A00]" />
                        <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00]">/ Availability check</div>
                    </div>
                    <form onSubmit={runAvailability} className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <select
                            value={avScreen?.id || ""}
                            onChange={(e) => setAvScreen(screens.find((s) => s.id === e.target.value) || null)}
                            data-testid="av-screen-select"
                            className="bg-[#0F1E3B] border border-white/10 text-white px-3 py-3 text-sm focus:border-[#FF5A00]"
                        >
                            <option value="">Select screen…</option>
                            {screens.map((s) => (
                                <option key={s.id} value={s.id}>{s.name} · {s.city}</option>
                            ))}
                        </select>
                        <input type="date" value={avRange.start}
                            onChange={(e) => setAvRange((r) => ({ ...r, start: e.target.value }))}
                            data-testid="av-start-date"
                            className="bg-[#0F1E3B] border border-white/10 text-white px-3 py-3 text-sm focus:border-[#FF5A00]" />
                        <input type="date" value={avRange.end}
                            onChange={(e) => setAvRange((r) => ({ ...r, end: e.target.value }))}
                            data-testid="av-end-date"
                            className="bg-[#0F1E3B] border border-white/10 text-white px-3 py-3 text-sm focus:border-[#FF5A00]" />
                        <button type="submit" disabled={avBusy}
                            data-testid="av-check-btn"
                            className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-3 text-xs font-semibold uppercase tracking-widest">
                            {avBusy ? <Loader2 size={14} className="animate-spin" /> : <CalendarSearch size={14} />} Check
                        </button>
                    </form>
                    <AnimatePresence>
                        {avResult && (
                            <motion.div
                                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                                className="mt-5 border-t border-white/10 pt-4"
                                data-testid="av-result"
                            >
                                {avResult.available ? (
                                    <div className="flex items-center gap-3 text-emerald-400 text-sm">
                                        <span className="inline-flex items-center justify-center w-6 h-6 bg-emerald-400/10 border border-emerald-400/40"><Check size={12} /></span>
                                        <span><strong>Available.</strong> No holds overlap the requested window.</span>
                                    </div>
                                ) : (
                                    <div>
                                        <div className="flex items-center gap-3 text-[#FF5A00] text-sm mb-3">
                                            <span className="inline-flex items-center justify-center w-6 h-6 bg-[#FF5A00]/10 border border-[#FF5A00]/50"><X size={12} /></span>
                                            <strong>Unavailable.</strong> {avResult.conflicts.length} overlapping hold(s).
                                        </div>
                                        <ul className="text-xs text-slate-300 space-y-1 font-mono-tech">
                                            {avResult.conflicts.map((c) => (
                                                <li key={c.booking_id}>
                                                    {c.start_date} → {c.end_date} · <span className="text-slate-500 uppercase">{c.status}</span>{c.label ? ` · ${c.label}` : ""}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Screen list */}
                <div className="mt-10">
                    {loading ? (
                        <div className="flex items-center justify-center py-16">
                            <Loader2 className="animate-spin text-[#FF5A00]" size={22} />
                        </div>
                    ) : screens.length === 0 ? (
                        <div className="border border-white/10 bg-[#1B2E55]/40 p-10 text-center" data-testid="screens-empty">
                            <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-500">/ Empty</div>
                            <div className="mt-3 text-white text-lg">No screens yet.</div>
                            <p className="mt-2 text-sm text-slate-400">Add your first digital billboard to unlock the booking pipeline.</p>
                            {isAdmin && (
                                <button onClick={openNew} className="mt-6 inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] text-white px-5 py-3 text-xs font-semibold uppercase tracking-widest">
                                    <Plus size={14} /> Add screen
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="overflow-x-auto border border-white/10">
                            <div className="p-3 border-b border-white/10">
                                <input value={invQuery} onChange={(e) => setInvQuery(e.target.value)}
                                    placeholder="Search inventory — name, city, owner, segment, maintenance…"
                                    data-testid="inventory-search-input"
                                    className="w-full max-w-md bg-[#1B2E55] border border-white/10 text-white px-3 py-2 text-xs placeholder:text-slate-500 focus:border-[#FF5A00]" />
                            </div>
                            <table className="w-full text-sm" data-testid="screens-table">
                                <thead className="bg-white/[0.03] text-slate-400">
                                    <tr className="font-mono-tech text-[10px] uppercase tracking-[0.25em]">
                                        <th className="text-left px-4 py-3">Name</th>
                                        <th className="text-left px-4 py-3">City</th>
                                        <th className="text-left px-4 py-3">Category</th>
                                        <th className="text-right px-4 py-3">Daily rate</th>
                                        <th className="text-left px-4 py-3">Status</th>
                                        {isAdmin && <th className="text-right px-4 py-3">Actions</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {screens.filter((s) => {
                                        const q = invQuery.trim().toLowerCase();
                                        if (!q) return true;
                                        return `${s.name} ${s.location} ${s.city} ${s.media_owner || ""} ${s.market_segment || ""} ${s.maintenance_status || ""}`.toLowerCase().includes(q);
                                    }).map((s) => (
                                        <tr key={s.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-3">
                                                    {s.photo_url ? (
                                                        <img src={s.photo_url} alt="" className="w-10 h-10 object-cover border border-white/10 shrink-0"
                                                            data-testid={`screen-row-${s.id}-photo`} />
                                                    ) : (
                                                        <div className="w-10 h-10 border border-white/10 bg-[#0F1E3B] shrink-0" />
                                                    )}
                                                    <div className="min-w-0">
                                                        <div className="text-white truncate">{s.name}</div>
                                                        <div className="text-xs text-slate-500 truncate">{s.location}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-slate-300">{s.city}</td>
                                            <td className="px-4 py-3">
                                                <span className="font-mono-tech text-[10px] uppercase tracking-widest text-[#FF5A00]">
                                                    {s.category === "cat1" ? "cat 1" : "cat 2"}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-right text-white">{money(s.daily_rate_cents)}</td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-block px-2 py-0.5 text-[10px] font-mono-tech uppercase tracking-widest border ${
                                                    s.status === "active" ? "text-emerald-400 border-emerald-400/40" : "text-slate-400 border-slate-500/40"
                                                }`}>{s.status}</span>
                                            </td>
                                            {isAdmin && (
                                                <td className="px-4 py-3 text-right">
                                                    <button onClick={() => openEdit(s)} data-testid={`screen-edit-${s.id}`}
                                                        className="inline-flex items-center gap-1 text-slate-400 hover:text-white px-2 py-1 text-xs">
                                                        <Pencil size={12} /> Edit
                                                    </button>
                                                    <button onClick={() => deleteScreen(s)} data-testid={`screen-delete-${s.id}`}
                                                        className="inline-flex items-center gap-1 text-slate-400 hover:text-[#FF5A00] px-2 py-1 text-xs">
                                                        <Trash2 size={12} /> Delete
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                </>
                )}

                {tab === "bookings" && <BookingsPanel />}
            </main>

            {/* Create/Edit modal */}
            <AnimatePresence>
                {editing && (
                    <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        onClick={closeForm}
                        data-testid="screen-form-modal"
                    >
                        <motion.form
                            onSubmit={saveScreen}
                            onClick={(e) => e.stopPropagation()}
                            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
                            className="w-full max-w-lg max-h-[90vh] flex flex-col bg-[#0F1E3B] border border-white/10"
                        >
                            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 shrink-0">
                                <div>
                                    <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00]">
                                        {editing.id ? "Edit screen" : "New screen"}
                                    </div>
                                    <div className="text-white font-display font-black text-xl">{editing.id ? editing.name : "Add to inventory"}</div>
                                </div>
                                <button type="button" onClick={closeForm} className="text-slate-400 hover:text-white" data-testid="screen-form-close">
                                    <X size={18} />
                                </button>
                            </div>
                            <div className="p-6 space-y-4 flex-1 overflow-y-auto">
                                <Field label="Name">
                                    <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                        data-testid="screen-input-name" required
                                        className="w-full bg-[#1B2E55] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#FF5A00]" />
                                </Field>
                                <Field label="Location">
                                    <input value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })}
                                        data-testid="screen-input-location" required
                                        className="w-full bg-[#1B2E55] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#FF5A00]" />
                                </Field>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="City">
                                        <input value={editing.city} onChange={(e) => setEditing({ ...editing, city: e.target.value })}
                                            data-testid="screen-input-city" required
                                            className="w-full bg-[#1B2E55] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#FF5A00]" />
                                    </Field>
                                    <Field label="Category">
                                        <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                                            data-testid="screen-input-category"
                                            className="w-full bg-[#1B2E55] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#FF5A00]">
                                            {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                                        </select>
                                    </Field>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Daily rate (AUD)">
                                        <input type="number" min="0" step="1" value={(editing.daily_rate_cents || 0) / 100}
                                            onChange={(e) => setEditing({ ...editing, daily_rate_cents: Math.round(Number(e.target.value) * 100) })}
                                            data-testid="screen-input-rate"
                                            className="w-full bg-[#1B2E55] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#FF5A00]" />
                                    </Field>
                                    <Field label="Status">
                                        <select value={editing.status || "active"} onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                                            data-testid="screen-input-status"
                                            className="w-full bg-[#1B2E55] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#FF5A00]">
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                    </Field>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Latitude (map pin)">
                                        <input type="number" step="any" min="-90" max="90"
                                            value={editing.lat ?? ""}
                                            onChange={(e) => setEditing({ ...editing, lat: e.target.value })}
                                            placeholder="-27.4698"
                                            data-testid="screen-input-lat"
                                            className="w-full bg-[#1B2E55] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#FF5A00]" />
                                    </Field>
                                    <Field label="Longitude (map pin)">
                                        <input type="number" step="any" min="-180" max="180"
                                            value={editing.lng ?? ""}
                                            onChange={(e) => setEditing({ ...editing, lng: e.target.value })}
                                            placeholder="153.0251"
                                            data-testid="screen-input-lng"
                                            className="w-full bg-[#1B2E55] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#FF5A00]" />
                                    </Field>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Market segment">
                                        <input value={editing.market_segment || ""} onChange={(e) => setEditing({ ...editing, market_segment: e.target.value })}
                                            placeholder="e.g. CBD retail, Freeway commuter" data-testid="screen-input-segment"
                                            className="w-full bg-[#1B2E55] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#FF5A00]" />
                                    </Field>
                                    <Field label="Maintenance status">
                                        <select value={editing.maintenance_status || "operational"} onChange={(e) => setEditing({ ...editing, maintenance_status: e.target.value })}
                                            data-testid="screen-input-maintenance"
                                            className="w-full bg-[#1B2E55] border border-white/10 text-white px-3 py-2.5 text-sm">
                                            {["operational", "scheduled_maintenance", "fault", "offline"].map((m) => <option key={m} value={m}>{m.replace("_", " ")}</option>)}
                                        </select>
                                    </Field>
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    <Field label="Resolution (px)">
                                        <input value={editing.resolution || ""} onChange={(e) => setEditing({ ...editing, resolution: e.target.value })}
                                            placeholder="1920x1080" data-testid="screen-input-resolution"
                                            className="w-full bg-[#1B2E55] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#FF5A00]" />
                                    </Field>
                                    <Field label="Physical size">
                                        <input value={editing.physical_size || ""} onChange={(e) => setEditing({ ...editing, physical_size: e.target.value })}
                                            placeholder="12m x 3m" data-testid="screen-input-size"
                                            className="w-full bg-[#1B2E55] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#FF5A00]" />
                                    </Field>
                                    <Field label="Format">
                                        <input value={editing.format || ""} onChange={(e) => setEditing({ ...editing, format: e.target.value })}
                                            placeholder="Landscape LED" data-testid="screen-input-format"
                                            className="w-full bg-[#1B2E55] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#FF5A00]" />
                                    </Field>
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    <Field label="Daily impressions (est.)">
                                        <input type="number" min="0" value={editing.daily_impressions ?? ""}
                                            onChange={(e) => setEditing({ ...editing, daily_impressions: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                                            data-testid="screen-input-impressions"
                                            className="w-full bg-[#1B2E55] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#FF5A00]" />
                                    </Field>
                                    <Field label="Cost price $ / day">
                                        <input type="number" min="0" step="1" value={editing.cost_price_cents == null ? "" : editing.cost_price_cents / 100}
                                            onChange={(e) => setEditing({ ...editing, cost_price_cents: e.target.value === "" ? null : Math.round(parseFloat(e.target.value || 0) * 100) })}
                                            data-testid="screen-input-cost"
                                            className="w-full bg-[#1B2E55] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#FF5A00]" />
                                    </Field>
                                    <Field label="Peak multiplier (quotes)">
                                        <input type="number" min="0.5" max="5" step="0.05" value={editing.peak_multiplier ?? ""}
                                            onChange={(e) => setEditing({ ...editing, peak_multiplier: e.target.value === "" ? null : parseFloat(e.target.value) })}
                                            placeholder="e.g. 1.25" data-testid="screen-input-peak-multiplier"
                                            className="w-full bg-[#1B2E55] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#FF5A00]" />
                                    </Field>
                                    <Field label="Plays per spot (PPP weight)" hint="Premium screens can consume more plays per spot. 1 = one spot uses one play.">
                                        <input type="number" min="1" max="1000" step="1" value={editing.play_weight ?? 1}
                                            onChange={(e) => setEditing({ ...editing, play_weight: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                                            placeholder="1" data-testid="screen-input-play-weight"
                                            className="w-full bg-[#1B2E55] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#FF5A00]" />
                                    </Field>
                                    <Field label="Media owner">
                                        <input value={editing.media_owner || ""} onChange={(e) => setEditing({ ...editing, media_owner: e.target.value })}
                                            data-testid="screen-input-owner"
                                            className="w-full bg-[#1B2E55] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#FF5A00]" />
                                    </Field>
                                </div>
                                <Field label="Media requirements">
                                    <input value={editing.media_requirements || ""} onChange={(e) => setEditing({ ...editing, media_requirements: e.target.value })}
                                        placeholder="e.g. MP4/JPG, max 50MB, no strobing content" data-testid="screen-input-requirements"
                                        className="w-full bg-[#1B2E55] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#FF5A00]" />
                                </Field>
                                <Field label="Notes">
                                    <textarea value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                                        rows={3} data-testid="screen-input-notes"
                                        className="w-full bg-[#1B2E55] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#FF5A00]" />
                                </Field>
                                <Field label={`Photos (up to 5)`}>
                                    <div className="space-y-3" data-testid="screen-photos-picker">
                                        <div className="grid grid-cols-5 gap-2">
                                            {(editing.photo_urls || []).map((url, idx) => (
                                                <div key={idx} className="relative group aspect-square border border-white/10 overflow-hidden">
                                                    <img src={url} alt="" className="w-full h-full object-cover"
                                                        data-testid={`screen-photo-preview-${idx}`} />
                                                    <button type="button"
                                                        onClick={() => setEditing((s) => ({
                                                            ...s,
                                                            photo_urls: (s.photo_urls || []).filter((_, i) => i !== idx),
                                                        }))}
                                                        data-testid={`screen-photo-remove-${idx}`}
                                                        aria-label="Remove photo"
                                                        className="absolute top-1 right-1 bg-black/70 hover:bg-[#FF5A00] text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <X size={10} />
                                                    </button>
                                                </div>
                                            ))}
                                            {(editing.photo_urls || []).length < 5 && (
                                                <label className="aspect-square border border-dashed border-white/20 hover:border-[#FF5A00] flex flex-col items-center justify-center cursor-pointer text-slate-500 hover:text-[#FF5A00] transition-colors"
                                                    data-testid="screen-photo-add">
                                                    <Plus size={16} />
                                                    <span className="text-[9px] font-mono-tech uppercase tracking-widest mt-1">Add</span>
                                                    <input type="file" accept="image/*" multiple
                                                        data-testid="screen-photo-file"
                                                        className="sr-only"
                                                        onChange={async (e) => {
                                                            const files = Array.from(e.target.files || []);
                                                            const slots = 5 - (editing.photo_urls || []).length;
                                                            const accepted = files.slice(0, slots);
                                                            for (const f of accepted) {
                                                                if (f.size > 400_000) {
                                                                    toast.error(`"${f.name}" is too large — keep each under 400 KB.`);
                                                                    continue;
                                                                }
                                                                const dataUrl = await new Promise((resolve, reject) => {
                                                                    const r = new FileReader();
                                                                    r.onload = () => resolve(r.result);
                                                                    r.onerror = reject;
                                                                    r.readAsDataURL(f);
                                                                });
                                                                setEditing((s) => ({
                                                                    ...s,
                                                                    photo_urls: [...(s.photo_urls || []), dataUrl].slice(0, 5),
                                                                }));
                                                            }
                                                            e.target.value = "";
                                                        }} />
                                                </label>
                                            )}
                                        </div>
                                        <div className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-500">
                                            {(editing.photo_urls || []).length}/5 · First photo is the primary thumbnail
                                        </div>
                                    </div>
                                </Field>
                            </div>
                            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/10 shrink-0">
                                <button type="button" onClick={closeForm} className="text-slate-400 hover:text-white text-xs font-mono-tech uppercase tracking-widest">
                                    Cancel
                                </button>
                                <button type="submit" disabled={busy}
                                    data-testid="screen-form-submit"
                                    className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-widest disabled:opacity-60">
                                    {busy ? <><Loader2 size={14} className="animate-spin" /> Saving</> : editing.id ? "Save changes" : "Add screen"}
                                </button>
                            </div>
                        </motion.form>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
        </PortalShell>
    );
}

const Field = ({ label, hint, children }) => (
    <div>
        <label className="block text-[10px] font-mono-tech uppercase tracking-[0.3em] text-slate-500 mb-1.5">{label}</label>
        {children}
        {hint && <div className="mt-1 text-[10px] text-slate-500">{hint}</div>}
    </div>
);

// ---------- Bookings tab ----------
const STATUS_STYLES = {
    hold: "text-amber-300 border-amber-400/40",
    confirmed: "text-emerald-300 border-emerald-400/50",
    waitlist: "text-[#FF5A00] border-[#FF5A00]/60",
    released: "text-slate-500 border-slate-600",
    reserved: "text-sky-300 border-sky-400/40",
};

const STATUS_FILTERS = ["all", "hold", "confirmed", "waitlist", "released"];

const BookingsPanel = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = filter === "all" ? {} : { status: filter };
            const { data } = await api.get("/screens/bookings/all", { params });
            setRows(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not load bookings.");
        } finally { setLoading(false); }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const counts = useMemo(() => {
        const c = { all: rows.length, hold: 0, confirmed: 0, waitlist: 0, released: 0 };
        rows.forEach((r) => { if (c[r.status] !== undefined) c[r.status]++; });
        return c;
    }, [rows]);

    return (
        <div className="mt-8" data-testid="bookings-panel">
            <div className="flex flex-wrap items-center gap-2 mb-5">
                {STATUS_FILTERS.map((s) => (
                    <button
                        key={s}
                        onClick={() => setFilter(s)}
                        data-testid={`bookings-filter-${s}`}
                        className={`px-3 py-1.5 text-[10px] font-mono-tech uppercase tracking-widest border transition-colors ${
                            filter === s
                                ? "border-[#FF5A00] bg-[#FF5A00]/10 text-white"
                                : "border-white/15 text-slate-400 hover:text-white hover:border-white/40"
                        }`}
                    >
                        {s} {filter === "all" && counts[s] !== undefined ? `· ${counts[s]}` : ""}
                    </button>
                ))}
            </div>
            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="animate-spin text-[#FF5A00]" size={22} />
                </div>
            ) : rows.length === 0 ? (
                <div className="border border-white/10 bg-[#1B2E55]/40 p-10 text-center" data-testid="bookings-empty">
                    <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-500">/ Empty</div>
                    <div className="mt-3 text-white text-lg">No bookings yet.</div>
                    <p className="mt-2 text-sm text-slate-400">Bookings appear once a client reserves screens through the wizard.</p>
                </div>
            ) : (
                <div className="overflow-x-auto border border-white/10">
                    <table className="w-full text-sm" data-testid="bookings-table">
                        <thead className="bg-white/[0.03] text-slate-400">
                            <tr className="font-mono-tech text-[10px] uppercase tracking-[0.25em]">
                                <th className="text-left px-4 py-3">Screen</th>
                                <th className="text-left px-4 py-3">Campaign</th>
                                <th className="text-left px-4 py-3">Window</th>
                                <th className="text-left px-4 py-3">Status</th>
                                <th className="text-left px-4 py-3">Note</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((b) => (
                                <tr key={b.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`booking-row-${b.id}`}>
                                    <td className="px-4 py-3">
                                        <div className="text-white truncate max-w-[220px]">{b.screen_name}</div>
                                        <div className="text-xs text-slate-500">{b.screen_city}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        {b.campaign_id ? (
                                            <Link to={`/dashboard/pipeline`} state={{ open: b.campaign_id }}
                                                data-testid={`booking-campaign-link-${b.id}`}
                                                className="text-white hover:text-[#FF5A00] inline-flex items-center gap-1">
                                                <span className="truncate max-w-[180px]">{b.campaign_business || "(campaign)"}</span>
                                                <ExternalLink size={10} />
                                            </Link>
                                        ) : (
                                            <span className="text-slate-500 text-xs font-mono-tech uppercase tracking-widest">Unassigned</span>
                                        )}
                                        {b.campaign_pipeline_status && (
                                            <div className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-500 mt-0.5">
                                                / {b.campaign_pipeline_status}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-slate-300 whitespace-nowrap">
                                        {b.start_date} → {b.end_date}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-block px-2 py-0.5 text-[10px] font-mono-tech uppercase tracking-widest border ${STATUS_STYLES[b.status] || "border-white/10 text-slate-300"}`}>
                                            {b.status}
                                        </span>
                                        {b.hold_expires_at && b.status === "hold" && (
                                            <div className="text-[10px] font-mono-tech text-amber-300 mt-1 inline-flex items-center gap-1">
                                                <Timer size={10} /> <HoldCountdown iso={b.hold_expires_at} />
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-slate-400 text-xs truncate max-w-[240px]">
                                        {b.label || "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const HoldCountdown = ({ iso }) => {
    const [tick, setTick] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setTick(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);
    const ms = new Date(iso).getTime() - tick;
    if (ms <= 0) return <span>expired</span>;
    const s = Math.floor(ms / 1000);
    const mm = Math.floor(s / 60).toString().padStart(2, "0");
    const ss = (s % 60).toString().padStart(2, "0");
    return <span>{mm}:{ss}</span>;
};


/* ─────────────────────── CSV bulk import ──────────────────────────── */
const CSV_TEMPLATE = "name,location,city,category,daily_rate_cents,status,format,media_owner,media_requirements,resolution,physical_size,daily_impressions,timezone,play_weight,cost_price_cents,lat,lng,notes,photo_url\n";

const ImportCsvButton = ({ onDone }) => {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const inputRef = useRef(null);

    const submit = async (file) => {
        if (!file) return;
        setBusy(true); setResult(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/screens/import-csv", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(data);
            const s = data?.summary || {};
            toast.success(`Imported ${s.created || 0} · skipped ${s.skipped || 0} · errors ${s.errors || 0}`);
            if (s.created) onDone?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "CSV import failed.");
        } finally { setBusy(false); }
    };

    const downloadTemplate = () => {
        const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "noxn-screens-template.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <>
            <button onClick={() => setOpen(true)} data-testid="screens-import-btn"
                className="inline-flex items-center gap-2 border border-white/15 hover:border-[#FF5A00] hover:text-[#FF5A00] text-slate-300 px-5 py-3 text-xs font-semibold uppercase tracking-widest transition-colors">
                <Upload size={14} /> Import CSV
            </button>

            {open && (
                <div className="fixed inset-0 z-[600] flex items-center justify-center px-6" data-testid="screens-import-modal">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !busy && setOpen(false)} />
                    <div className="relative bg-[#0F1E3B] border border-white/10 rounded-2xl w-full max-w-lg p-6">
                        <button onClick={() => !busy && setOpen(false)} className="absolute top-4 right-4 text-slate-400 hover:text-white" aria-label="Close">
                            <X size={16} />
                        </button>
                        <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-[#FF5A00] mb-2">/ Bulk import</div>
                        <h3 className="font-display text-2xl text-white font-medium">Import screens from CSV</h3>
                        <p className="mt-2 text-xs text-slate-400">
                            Duplicate <code className="text-slate-300">name</code> rows are skipped. Empty numeric fields are ignored.
                        </p>

                        <div className="mt-5 flex flex-col gap-3">
                            <button onClick={downloadTemplate} data-testid="screens-import-template"
                                className="inline-flex items-center gap-2 text-[11px] font-mono-tech uppercase tracking-widest text-slate-400 hover:text-white transition-colors">
                                <ExternalLink size={11} /> Download template
                            </button>

                            <input ref={inputRef} type="file" accept=".csv,text/csv" data-testid="screens-import-file"
                                onChange={(e) => submit(e.target.files?.[0])}
                                className="text-xs text-slate-300 file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[#FF5A00] file:text-white file:text-xs file:font-semibold file:uppercase file:tracking-widest hover:file:bg-[#E65200] cursor-pointer" />

                            {busy && (
                                <div className="flex items-center gap-2 text-xs text-slate-400">
                                    <Loader2 size={12} className="animate-spin" /> Importing…
                                </div>
                            )}

                            {result && (
                                <div className="mt-2 rounded-xl border border-white/10 bg-[#1B2E55]/50 p-4 text-xs" data-testid="screens-import-result">
                                    <div className="flex flex-wrap gap-x-5 gap-y-1 text-slate-300">
                                        <span><Check size={11} className="inline text-emerald-400" /> Created <strong className="text-white">{result.summary?.created || 0}</strong></span>
                                        <span>Skipped <strong className="text-white">{result.summary?.skipped || 0}</strong></span>
                                        <span>Errors <strong className={result.summary?.errors ? "text-[#FF5A00]" : "text-white"}>{result.summary?.errors || 0}</strong></span>
                                    </div>
                                    {(result.errors || []).slice(0, 5).map((er, i) => (
                                        <div key={i} className="mt-2 text-[11px] text-[#FF5A00]">Row {er.row}: {er.error}</div>
                                    ))}
                                    {(result.skipped || []).slice(0, 3).map((sk, i) => (
                                        <div key={i} className="mt-1 text-[11px] text-slate-500">Row {sk.row}: {sk.reason} ({sk.name})</div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
